import React from 'react';
import { ErrorPage } from './error-page';
import { AIError } from '@/types/ai';
import { createErrorBoundaryHandler, ErrorSeverity } from '@/lib/error-handler';

interface Props {
  children: React.ReactNode;
  componentName?: string; // Optional component name for better error tracking
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Save error info for better debugging
    this.setState({ errorInfo });
    
    // Use our centralized error handler
    const handler = createErrorBoundaryHandler(this.props.componentName || 'UnknownComponent');
    handler(error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const errorObj: AIError = {
        type: 'ERROR',
        error: this.state.error?.message || 'Unknown error occurred',
        timestamp: new Date().toISOString()
      };
      
      return (
        <ErrorPage
          code="500"
          title="Something went wrong"
          subtitle="We encountered an unexpected error."
          error={errorObj}
          onRetry={() => this.setState({ hasError: false })}
        />
      );
    }

    return this.props.children;
  }
}
